import LrDialog, { LrDialogBaseProps } from 'components/LrDialog';
import * as React from 'react';
import { Form, Formik, Field } from 'formik';
import Grid from '@mui/material/Unstable_Grid2';
import { TextInput } from 'components/Input';
import { dateMask } from 'helpers/masks';
import { Margin, Spacing } from 'components/Layout';
import LrButton from 'components/LrButton/components/Button';
import { InspectionData } from 'types/location.type';
import { useState } from 'react';
import Typography from '@mui/material/Typography';
import { LrNotes } from 'components/LrNotes';
import { ACTION_TYPES } from 'constants/index';
import { useSelector } from 'react-redux';
import { getLogTypesByCode } from 'application/redux/selectors';
import { LogOptions } from '../../../../../types/logs.types';
import * as Yup from 'yup';
import { FormikHelpers } from 'formik/dist/types';

const inspectionDataSchema = Yup.object().shape({
    receivedDate: Yup.string().min(10, 'Must be a valid date').max(10, 'Must be a valid date').nullable(),
    paidDate: Yup.string().min(10, 'Must be a valid date').max(10, 'Must be a valid date').nullable(),
});

interface Props extends LrDialogBaseProps {
    inspectionReportData?: InspectionData;
    locationName: string;
    locationAddress?: string;
    onInspectionSave?: (submittedInspection: InspectionData) => void;
}

const LocationInspectionDialog: React.FunctionComponent<Props> = (props) => {
    const { inspectionReportData, locationName, locationAddress, caption, isOpen, onCancel, onInspectionSave } = props;
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const dialogTitle = `Location Inspection Report`;
    const logTypes = useSelector(getLogTypesByCode);
    const canEdit = true;
    const notesOptions = { inspectionId: String(inspectionReportData?.id || '') } as LogOptions;
    const onDialogSubmit = (data: InspectionData, helpers: FormikHelpers<InspectionData>) => {
        setIsEditing(false);
        onInspectionSave && onInspectionSave(data);
        helpers.setSubmitting(false);
    };

    const onDialogCancel = () => {
        onCancel && onCancel();
    };

    return (
        <LrDialog
            isOpen={isOpen}
            caption={caption}
            onCancel={onDialogCancel}
            closeButton
            title={dialogTitle}
            width="800px"
        >
            <>
                <Typography variant={'h2'}>{locationName}</Typography>
                <Margin left="12">
                    <Typography variant={'subtitle1'}>{locationAddress}</Typography>
                </Margin>
                <Formik
                    initialValues={inspectionReportData || ({} as InspectionData)}
                    isInitialValid={false}
                    onSubmit={onDialogSubmit}
                    validationSchema={isEditing ? inspectionDataSchema : undefined}
                    enableReinitialize
                >
                    {({ errors, isValid, isSubmitting, touched, values }) => (
                        <Form>
                            <Grid container spacing={1}>
                                <Grid xs={4}>
                                    <Field
                                        as={TextInput}
                                        name="requestedDate"
                                        title="Inspection Requested Date"
                                        readOnly={true}
                                        errorMessage={touched.requestedDate && errors.requestedDate}
                                    />
                                </Grid>
                                <Grid xs={4}>
                                    <Field
                                        as={TextInput}
                                        name="receivedDate"
                                        title="Inspection Received Date"
                                        readOnly={!isEditing}
                                        errorMessage={touched.receivedDate && errors.receivedDate}
                                        mask={dateMask}
                                    />
                                </Grid>
                                <Grid xs={4}>
                                    <Field
                                        as={TextInput}
                                        name="paidDate"
                                        title="Inspection Paid Date"
                                        readOnly={!isEditing}
                                        errorMessage={touched.paidDate && errors.paidDate}
                                        mask={dateMask}
                                    />
                                </Grid>
                            </Grid>

                            <Spacing height="32px" />
                            <Grid container spacing={0} alignItems="center">
                                {isEditing && (
                                    <>
                                        <LrButton lrType="secondary" onClick={() => setIsEditing(false)}>
                                            {'Cancel'}
                                        </LrButton>
                                        <LrButton
                                            lrType="primary"
                                            type="submit"
                                            disabled={!isValid}
                                            isLoading={isSubmitting}
                                        >
                                            {'Save'}
                                        </LrButton>
                                    </>
                                )}
                                {!isEditing && (
                                    <LrButton
                                        lrType="primary"
                                        disabled={!canEdit}
                                        isLoading={isSubmitting}
                                        onClick={() => setIsEditing(true)}
                                    >
                                        {'Edit'}
                                    </LrButton>
                                )}
                            </Grid>
                        </Form>
                    )}
                </Formik>
                <LrNotes
                    canCreate
                    emptyText={'There are no Notes for this Inspection yet.'}
                    title="Inspection Notes"
                    noteCreateTypeId={logTypes.INSPECTION_REPORT.id}
                    options={notesOptions}
                    notesActionType={ACTION_TYPES.INSPECTION_REPORT}
                />
            </>
        </LrDialog>
    );
};

export default LocationInspectionDialog;
